import { useState } from 'react';
import { useAuth } from '../auth/AuthContext';
import { errorMessageFor } from '../auth/error-message';
import { adminApi as defaultAdminApi } from './admin-api';
import { firstOfNextMonth, proposeCalendarPeriod, todayInArgentina } from '../lib/period-proposal';

/**
 * "Nuevo período" form inside a member's detail (spec "Administrador":
 * "creación del próximo período a partir de un plan activo, con inicio
 * explícito y fin opcional"). Only ACTIVE plans are offered — an inactive
 * plan can't open new periods (server: 409 `plan_inactive`) — so this filters
 * them out of the `<select>` up front instead of letting the admin pick one
 * and only finding out from a rejected request.
 *
 * Mes calendario (spec 2026-10-08): cuando llega `memberships`, el formulario
 * propone el primer mes calendario sin cubrir (día 1 -> día 1 del mes
 * siguiente). Mientras el admin no toque "Hasta", ese campo sigue al inicio.
 * Sin `memberships` se conserva el comportamiento anterior: `latestEndDate`
 * precarga el inicio y "Hasta" arranca vacío (el servidor calcula el fin).
 * "Hasta, sin incluir" hace explícita la convención de fin exclusivo.
 * The explicit end is only included in the payload when the field isn't
 * empty; `userId`/`planId`/`startDate` are otherwise the only keys sent,
 * exactly the `POST /memberships` contract (amount/planName always come from
 * the plan server-side).
 */
export function MembershipForm({
  api = defaultAdminApi,
  memberId,
  plans,
  latestEndDate,
  memberships,
  today,
  onCreated,
  onCancel,
}) {
  const { handleApiError } = useAuth();
  const activePlans = (plans || []).filter((plan) => plan.isActive);
  const [planId, setPlanId] = useState('');
  const [proposal] = useState(() => (Array.isArray(memberships)
    ? proposeCalendarPeriod(memberships, today || todayInArgentina())
    : { startDate: latestEndDate || '', endDate: '' }));
  const [startDate, setStartDate] = useState(proposal.startDate);
  const [endDate, setEndDate] = useState(proposal.endDate);
  // "Hasta" acompaña al inicio (día 1 del mes siguiente) hasta que el admin lo edita a mano.
  const [endTouched, setEndTouched] = useState(!Array.isArray(memberships));

  function handleStartChange(value) {
    setStartDate(value);
    if (!endTouched) setEndDate(firstOfNextMonth(value));
  }
  const [error, setError] = useState('');
  const [submitting, setSubmitting] = useState(false);

  async function handleSubmit(event) {
    event.preventDefault();
    if (submitting) return;
    setError('');

    if (!planId) {
      setError('Elegí un plan.');
      return;
    }
    if (!startDate) {
      setError('Ingresá la fecha de inicio.');
      return;
    }

    const payload = { userId: memberId, planId, startDate };
    if (endDate) payload.endDate = endDate;

    setSubmitting(true);
    try {
      const created = await api.createMembership(payload);
      onCreated(created && created.membership);
    } catch (caught) {
      const outcome = handleApiError(caught);
      if (outcome.handled) return;
      setError(errorMessageFor(caught));
      setSubmitting(false);
    }
  }

  return (
    <form className="gym-form gym-card gym-admin-membership-form" onSubmit={handleSubmit} noValidate aria-label="Nuevo período">
      <div className="gym-admin-detail-sheet__header">
        <h2>Nuevo período</h2>
        {onCancel && <button type="button" className="gym-admin-detail-sheet__close" aria-label="Cerrar" disabled={submitting} onClick={onCancel}>×</button>}
      </div>
      {error && <p role="alert" className="gym-form__error">{error}</p>}

      <div className="gym-field">
        <label htmlFor="membership-plan" className="gym-field__label">Plan</label>
        <select
          id="membership-plan"
          name="planId"
          required
          value={planId}
          disabled={submitting}
          onChange={(event) => setPlanId(event.target.value)}
          className="gym-input"
        >
          <option value="">Elegí un plan</option>
          {activePlans.map((plan) => (
            <option key={plan.id} value={plan.id}>{plan.name}</option>
          ))}
        </select>
      </div>

      <div className="gym-field">
        <label htmlFor="membership-start" className="gym-field__label">Fecha de inicio</label>
        <input
          id="membership-start"
          name="startDate"
          type="date"
          required
          value={startDate}
          disabled={submitting}
          onChange={(event) => handleStartChange(event.target.value)}
          className="gym-input"
        />
      </div>

      <div className="gym-field">
        <label htmlFor="membership-end" className="gym-field__label">Hasta, sin incluir</label>
        <input
          id="membership-end"
          name="endDate"
          type="date"
          value={endDate}
          disabled={submitting}
          onChange={(event) => { setEndTouched(true); setEndDate(event.target.value); }}
          className="gym-input"
        />
        <p className="gym-admin-field-hint">Dejalo vacío para que termine el día 1 del mes siguiente.</p>
      </div>

      <div className="gym-admin-form-actions">
        <button type="submit" disabled={submitting} className="gym-button gym-button--primary">
          {submitting ? 'Creando…' : 'Crear período'}
        </button>
        {onCancel && (
          <button type="button" className="gym-button gym-button--secondary" disabled={submitting} onClick={onCancel}>
            Cancelar
          </button>
        )}
      </div>
    </form>
  );
}
